import { QuantityValidationErrorMessage, QuantityValidationErrorType } from "ts-types/api.enums";

const TIME_REGEX = "^(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-5][0-9]$";
const IBAN_REGEX = "(CH|LI)[a-zA-Z0-9]{19}$";
const EMAIL_REGEX = "(^$|^(([^<>()[\\]\\\\.,;:\\s@\"]+(\\.[^<>()[\\]\\\\.,;:\\s@\"]+)*)|(\".+\"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$)";
const AVH_REGEX = "^756\\.(\\d{4})\\.(\\d{4})\\.(\\d{2})$";
const CT_REGEX = "^(\\d{1,2})\\.(\\d{1,2})$";
const CCD_REGEX = "^\\d{4}$";
const TWINT_TRANSACTION_ID_REGEX = "^[a-zA-Z0-9]{16}$";
const ZIPCITY_REGEX = "^\\d{2,10}\\s+\\S+.*$";

export const required = (value: any) => {
  if (typeof value == "string") {
    return value.trim() != "" ? undefined : "Required";
  } else if (typeof value == "number") {
    return undefined;
  } else if (Array.isArray(value) && !value.length) {
    return "Required";
  } else {
    return value ? undefined : "Required";
  }
};

export const timeValidator = (value: string): string | undefined => {

  if (value) {
    return value.match(TIME_REGEX) ? undefined : "Required";
  }

  return undefined;
};

export const ibanValidator = (value: string): string | undefined => {

  if (value) {
    return value.match(IBAN_REGEX) ? undefined : "Required";
  }

  return undefined;
};

export const composeValidators = (...validators: any) => (value: any) =>
  validators.reduce((error: any, validator: any) => error || validator(value), undefined);

export const ctValidator = (value: string): string | undefined => {

  if (value) {
    if (!value.match(CT_REGEX)) {
      return "You have entered an incorrect CT value";
    }
  }
  return undefined;
};

export const mocValidator = (value: string): string | undefined => {

  if (value) {
    if (!value.match(CT_REGEX)) {
      return "You have entered an incorrect MOC value";
    }
  }
  return undefined;
};

export const ccdValidator = (value: string): string | undefined => {

  const required = value ? undefined : "required";
  if (required) {
    return required;
  }

  if (value) {
    if (!value.match(CCD_REGEX)) {
      return "You have entered an incorrect credit card digits.";
    }
  }
  return undefined;
};

export const twintTransactionIdValidator = (value: string): string | undefined => {

  const required = value ? undefined : "required";
  if (required) {
    return required;
  }

  if (value) {
    if (!value.match(TWINT_TRANSACTION_ID_REGEX)) {
      return "You have entered an incorrect Twint transaction number.";
    }
  }
  return undefined;
};

export const ahvValidator = (value: string): string | undefined => {

  if (value) {
    if (!value.match(AVH_REGEX)) {
      return "You have entered an incorrect ahv";
    } else if (computeAhvn13CheckDigit(value) !== parseInt(value.substr(-1))) {
      return "Number not valid";
    }
  }
  return undefined;
};

const computeAhvn13CheckDigit = (value: string): number | undefined => {

  const ahvn13str = value.replace(/\D/g, "");
  const chars = ahvn13str.substring(0, ahvn13str.length - 1).split("").reverse();

  let crosssum = 0;
  for (let i = 0; i < chars.length; i++) {
    if (0 == i % 2) {
      crosssum += parseInt(chars[i]) * 3;
    } else {
      crosssum += parseInt(chars[i]);
    }
  }
  const ceilSum = Math.ceil(crosssum / 10) * 10;
  return ceilSum - crosssum;

};

export const zipCityValidator = (value: string): string | undefined => {

  if (value) {
    return value.match(ZIPCITY_REGEX) ? undefined : "Must be in a \"{zip} {city}\" format. E.g. \"9001 St. Gallen\".";
  }

  return undefined;
};

export const mustBeNumber = (value: any) => {

  if (value) {
    if (!isValueValidNumber(value)) {
      return "validator.mustBeNumber";
    }
  }

  return undefined;
};

export const emailPatientValidator = (value: string): string | undefined => {

  if (value) {
    return value.match(EMAIL_REGEX) ? undefined : "You have entered an incorrect email";
  }

  return undefined;
};

export const emailValidator = (value: string): string | undefined => {

  if (value) {
    return value.match(EMAIL_REGEX) ? undefined : "InvalidEmail";
  }

  return undefined;
};

export const multipleEmailsValidator = (value: string): string | undefined => {

  if (value) {
    let emails = value.split(";");
    for (let i = 0; i < emails.length; i++) {
      if (emailValidator(emails[i].trim()) === "InvalidEmail") {
        return "InvalidEmail";
      }
    }
  }
  return undefined;
};

export const mustBePositiveNumber = (value: any) => {

  if (+value < 0) {
    return "validator.mustBePositiveNumber";
  }

  return undefined;
};

export const mustNotBeZero = (value: any) => {

  if (value) {
    if (value == 0) {
      return "validator.mustNotBeZero";
    }
  }

  return undefined;
};

export const maxDigits = (numOfDigits: number) => (value: any) => {
  return value.length > numOfDigits ? " validator.maxDigits " + numOfDigits : undefined;
};

export const maxChars = (numOfChars: number) => (value: string) => {
  return value && value.length > numOfChars ? " validator.maxChars " + numOfChars : undefined;
};

export const validateOnlyIfPresent = (composedValidators: (value: string) => string | undefined) => (value: any) => {
  return value ? composedValidators(value) : undefined;
};

export const isValueEmpty = (value: any): boolean => value == undefined || value == "";
export const isValueValidNumber = (value: string | number): boolean => +value == +value && !value.toString().includes("e");

export interface TarmedErrorMessage {
  messageKey: string,
  procedureCode?: string,
  remainingQuantity?: number,
  maxQuantity?: number,
  numDays?: number,
}

const getMessageByErrorType = (
  errorMessageKey: string,
  errorType: QuantityValidationErrorType,
  remainingQuantity?: number,
  maxQuantity?: number,
  numDays?: number
): TarmedErrorMessage => {

  switch (errorType) {
    case QuantityValidationErrorType.HAS_ALL_REMAINING: {
      return {
        messageKey: errorMessageKey,
        maxQuantity: maxQuantity,
        numDays: numDays
      };
    }
    case QuantityValidationErrorType.HAS_SOME_REMAINING: {
      return {
        messageKey: errorMessageKey + "Remaining",
        remainingQuantity: remainingQuantity,
        maxQuantity: maxQuantity,
        numDays: numDays
      };
    }
    case QuantityValidationErrorType.HAS_NO_REMAINING: {
      return {
        messageKey: errorMessageKey + "NoRemaining",
        maxQuantity: maxQuantity,
        numDays: numDays
      };
    }
    default: {
      return {
        messageKey: errorMessageKey,
        maxQuantity: maxQuantity,
        numDays: numDays
      };
    }
  }
};

export const generateTarmedErrorMessage = (
  errorMessage: QuantityValidationErrorMessage,
  errorType: QuantityValidationErrorType,
  remainingQuantity?: number,
  maxQuantity?: number,
  numDays?: number
): TarmedErrorMessage => {

  switch (errorMessage) {
    case QuantityValidationErrorMessage.NOT_VALID_FOR_ELECTRONIC_DATA_TRANSFER: {
      return {
        messageKey: "tarmedValidation.notValidForElectronicDataTransfer"
      };
    }
    case QuantityValidationErrorMessage.OBLIGATORY: {
      return {
        messageKey: "tarmedValidation.obligatory"
      };
    }
    case QuantityValidationErrorMessage.X_QUANTITY_PER_PROCEDURE: {
      return getMessageByErrorType("tarmedValidation.quantityPerProcedure", errorType, remainingQuantity, maxQuantity, numDays);
    }
    case QuantityValidationErrorMessage.X_QUANTITY_IN_ONE_DAY: {
      return getMessageByErrorType("tarmedValidation.quantityPerDay", errorType, remainingQuantity, maxQuantity, numDays);
    }
    case QuantityValidationErrorMessage.X_QUANTITY_IN_X_DAYS: {
      return getMessageByErrorType("tarmedValidation.quantityPerXDays", errorType, remainingQuantity, maxQuantity, numDays);
    }
    case QuantityValidationErrorMessage.X_QUANTITY_IN_ONE_WEEK: {
      return getMessageByErrorType("tarmedValidation.quantityPerWeek", errorType, remainingQuantity, maxQuantity, numDays);
    }
    case QuantityValidationErrorMessage.X_QUANTITY_IN_ONE_MONTH: {
      return getMessageByErrorType("tarmedValidation.quantityPerMonth", errorType, remainingQuantity, maxQuantity, numDays);
    }
    case QuantityValidationErrorMessage.X_QUANTITY_IN_ONE_YEAR: {
      return getMessageByErrorType("tarmedValidation.quantityPerYear", errorType, remainingQuantity, maxQuantity, numDays);
    }
    case QuantityValidationErrorMessage.X_QUANTITY_PER_PATIENT: {
      return getMessageByErrorType("tarmedValidation.quantityPerPatient", errorType, remainingQuantity, maxQuantity, numDays);
    }
    case QuantityValidationErrorMessage.X_QUANTITY_PER_CONSULTATION: {
      return {
        messageKey: "tarmedValidation.quantityPerConsultation",
        maxQuantity: maxQuantity
      };
    }
    case QuantityValidationErrorMessage.X_QUANTITY_PER_TREATMENT: {
      return getMessageByErrorType("tarmedValidation.quantityPerTreatment", errorType, remainingQuantity, maxQuantity);
    }
  }
};

type GetMessage = (key: string) => string;
type GetMessageWithNamedParams = (key: string, params: any) => string;

export const formatTarmedErrorMessage = (
  errorMessage: TarmedErrorMessage,
  getMessage: GetMessage,
  getMessageWithNamedParams: GetMessageWithNamedParams
): string => {

  if (errorMessage.maxQuantity) {
    return getMessageWithNamedParams(errorMessage.messageKey, {
      maxQuantity: errorMessage.maxQuantity,
      remainingQuantity: errorMessage.remainingQuantity,
      numDays: errorMessage.numDays
    });
  } else if (errorMessage.procedureCode) {
    return getMessageWithNamedParams(errorMessage.messageKey, {
      procedureCode: errorMessage.procedureCode,
      numDays: errorMessage.numDays
    });
  }

  return getMessage(errorMessage.messageKey);
};

export const formatTarmedErrorMessages = (
  errorMessages: Array<TarmedErrorMessage>,
  getMessage: GetMessage,
  getMessageWithNamedParams: GetMessageWithNamedParams
): Array<string> => {
  return errorMessages.map(errorMessage => formatTarmedErrorMessage(errorMessage, getMessage, getMessageWithNamedParams));
};
